import type { ColumnDef } from "@tanstack/react-table";
import { RefreshCw, Trash2 } from "lucide-react";
import { Typography } from "@/components/ui/atoms/Typography";
import { StatusPill } from "@/components/ui/atoms/StatusPill";
import { dataTableSortHeader } from "@/components/ui/molecules/DataTable";
import type { JobOffer } from "@/models/job";
import { getJobStatusVariant } from "@/components/ui/atoms/StatusPill/statusPill.utils";
import { Button } from "@/components/ui/atoms/Button";

export interface DeletableJobTableRow {
  id: string;
  companyName: string;
  creatorEmail: string;
  jobTitle: string;
  statusDisplay: string;
  statusRaw: string;
  job: JobOffer;
}

export function mapJobOfferToDeletableRow(job: JobOffer): DeletableJobTableRow {
  return {
    id: job.id,
    companyName: job.companyName || "N/A",
    creatorEmail: job.creator_email || "N/A",
    jobTitle: job.title,
    statusDisplay: job.status?.replace(/_/g, " ") || "Unknown",
    statusRaw: job.status || "",
    job,
  };
}

export interface DeletableJobsTableColumnsOptions {
  onReload: (jobId: string, jobTitle: string) => void;
  onDelete: (jobId: string, jobTitle: string) => void;
}

export function createDeletableJobsTableColumns({
  onReload,
  onDelete,
}: DeletableJobsTableColumnsOptions): ColumnDef<DeletableJobTableRow>[] {
  return [
    {
      accessorKey: "companyName",
      meta: { label: "Company" },
      header: dataTableSortHeader("Company"),
      size: 200,
      minSize: 160,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <Typography variant="body-sm" weight="semibold" color="primary">
            {row.original.companyName}
          </Typography>
          <Typography variant="body-sm" weight="medium" color="muted">
            {row.original.creatorEmail}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: "jobTitle",
      meta: { label: "Job title" },
      header: dataTableSortHeader("Job title"),
      size: 220,
      minSize: 180,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="semibold" color="primary">
          {row.original.jobTitle}
        </Typography>
      ),
    },
    {
      accessorKey: "statusDisplay",
      meta: { label: "Status" },
      header: dataTableSortHeader("Status"),
      size: 140,
      minSize: 120,
      cell: ({ row }) => (
        <StatusPill
          variant={getJobStatusVariant(row.original.statusRaw)}
        >
          {row.original.statusDisplay}
        </StatusPill>
      ),
    },
    {
      id: "actions",
      meta: { label: "Actions" },
      header: () => (
        <span className="block whitespace-normal break-words text-center font-heading text-xs font-bold uppercase leading-snug tracking-wider text-dark-invirtus">
          Actions
        </span>
      ),
      size: 200,
      minSize: 180,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const { id, title } = row.original.job;
        return (
          <div className="flex flex-wrap justify-center gap-1.5">
            <Button 
              type="contained"
              intent="success-soft"
              size="xs"
              label="Reload"
              onClick={() => onReload(id, title)}
              icon={<RefreshCw />}
            />
            <Button 
              type="contained"
              intent="danger-soft"
              size="xs"
              label="Delete"
              onClick={() => onDelete(id, title)}
              icon={<Trash2 />}
            />
          </div>
        );
      },
    },
  ];
}
